import { format, parse, isValid } from 'date-fns';
import { createContext, use, useMemo, useState } from 'react';

import type { DateTimePickerContextValue, DateTimePickerMode, DateTimePickerRootProps, DateTimeValue } from './DateTimePicker.type';

const DateTimePickerContext = createContext<DateTimePickerContextValue | null>(null);

export function useDateTimePicker() {
  const context = use(DateTimePickerContext);
  if (!context) {
    throw new Error('useDateTimePicker must be used within a DateTimePickerRoot');
  }
  return context;
}

/**
 * Check if time string is valid HH:mm format
 */
export function isValidTime(time?: string): boolean {
  if (!time) return false;
  return isValid(parse(time, 'HH:mm', new Date()));
}

/**
 * Get current time as HH:mm string
 */
export function getCurrentTime(): string {
  return format(new Date(), 'HH:mm');
}

/**
 * Combine date and time into ISO string
 */
export function combineDateTime(date: Date, time?: string): string {
  const newDate = new Date(date);
  const parts: string[] = time?.split(':') || [];
  const hours: number = parseInt(parts[0] || '', 10);
  const minutes: number = parseInt(parts[1] || '', 10);

  if (!isNaN(hours) || !isNaN(minutes)) {
    newDate.setHours(isNaN(hours) ? 0 : hours, isNaN(minutes) ? 0 : minutes, 0, 0);
  } else {
    const now = new Date();
    newDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
  }
  return newDate.toISOString();
}

/**
 * Parse ISO string to DateTimeValue
 */
export function parseISOToDateTimeValue(isoString?: string | null): DateTimeValue | undefined {
  if (!isoString) return undefined;
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return undefined;
    return {
      date,
      time: format(date, 'HH:mm'),
    };
  } catch {
    return undefined;
  }
}

/**
 * Convert DateTimeValue to ISO string
 */
export function dateTimeValueToISO(value?: DateTimeValue): string | null {
  if (!value?.date) return null;
  return combineDateTime(value.date, value.time);
}

export function DateTimePicker(dateTimePickerRootProps: DateTimePickerRootProps) {
  const { children, defaultMode, value, onChange, rangeValue, onRangeChange, numberOfMonths = 2, matcher } = dateTimePickerRootProps;

  const [mode, setMode] = useState<DateTimePickerMode>(defaultMode ?? 'range');

  // Computed values
  const isRangeMode: boolean = mode === 'range' || mode === 'start' || mode === 'end';
  const hasStartDate: boolean = Boolean(rangeValue?.from?.date);
  const hasEndDate: boolean = Boolean(rangeValue?.to?.date);

  const contextValue = useMemo<DateTimePickerContextValue>(
    () => ({
      mode,
      setMode,
      value,
      onChange,
      rangeValue,
      onRangeChange,
      numberOfMonths,
      matcher,
      isRangeMode,
      hasStartDate,
      hasEndDate,
    }),
    [mode, setMode, value, onChange, rangeValue, onRangeChange, numberOfMonths, matcher, isRangeMode, hasStartDate, hasEndDate]
  );

  return <DateTimePickerContext value={contextValue}>{children}</DateTimePickerContext>;
}
